import {
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Tooltip,
} from "@jhool-io/fe-components";
import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";

import ExpiringPoliciesList from "../ExpiringPoliciesList/ExpiringPoliciesList";
import {
    ExpiryDateRange,
    IBillingOverViewResponse,
} from "../../types/dashboard.types";
import { IApiResponse } from "../../../../utils/types/api-response";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";

import { useFetchExpiringPolicies } from "../../hooks/dashboard.queries";
import Loader from "../../../../shared-ui/Loader/Loader";
import InfoIcon from "../../../../icons/Info";
import { getInsuranceProviderIdsFilter } from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";

interface BillingStats {
    billingStats: IApiResponse<IBillingOverViewResponse> | undefined;
    isLoading: boolean;
}
export default function BillingStatCards({
    billingStats,
    isLoading,
}: BillingStats) {
    const [showPolicies, setShowPolicies] = React.useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const dateFilter = searchParams.get("expiry_date") || "this_month";
    const insuranceProviderIdsFilter =
        getInsuranceProviderIdsFilter(searchParams);

    const { data: expiringPolicyData, isLoading: expiringPolicyLoading } =
        useFetchExpiringPolicies({
            custom_date: dateFilter,
            insurance_provider_ids: insuranceProviderIdsFilter,
            sort_attribute: "accumulations_end_date",
            sort_order: "DESC",
        });

    const handlePolicyModalToggle = () => {
        setShowPolicies((state) => !state);
    };

    const handleExpiryDateRangeFilter = (filter: string) => {
        searchParams.set("expiry_date", filter);
        setSearchParams(searchParams);
    };

    return (
        <>
            <Dialog open={showPolicies}>
                <DialogContent
                    handleCloseDialog={handlePolicyModalToggle}
                    showFooter={false}
                    classNames="w-auto"
                    wrapperClass="w-[1186px]"
                >
                    <ExpiringPoliciesList />
                </DialogContent>
            </Dialog>
            {(isLoading || expiringPolicyLoading) && <Loader />}

            {!isLoading && expiringPolicyData && (
                <div className="grid grid-cols-[repeat(3,minmax(400px,1fr))] md:grid-cols-[repeat(3,minmax(500px,1fr))] overflow-x-auto gap-x-[--spacing-items]">
                    <Card classNames="bg-[--bg-encounter]">
                        <div className="mb-[--spacing-items] flex items-center justify-between">
                            <div className="flex items-center gap-x-2">
                                <h3 className="font-bold text-base md:text-base">
                                    Recent remits
                                </h3>
                                <Tooltip content="Recent remits shows a list of recently received payments from insurance companies.">
                                    <Button size="auto" variant="normal">
                                        <InfoIcon />
                                    </Button>
                                </Tooltip>
                            </div>
                            <Link
                                to="/remits?navLinkInUrl=claims"
                                className="font-semibold text-textLink text-small"
                            >
                                View all remits
                            </Link>
                        </div>
                        <p className="text-textSec text-caption font-medium block mb-2">
                            Total Remits
                        </p>
                        <p className="font-bold text-titleLg block">
                            {billingStats?.data?.total_remits || 0}
                        </p>
                        <div className="mt-[--spacing-header]">
                            <div className="rounded-md bg-[#F6E7EF] py-6 px-8 grid grid-cols-3 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                <span>Date of service</span>
                                <span>Client</span>
                                <span>Amount</span>
                            </div>
                            {billingStats?.data?.remits.length === 0 && (
                                <div className="flex flex-col gap-y-[--spacing-items]">
                                    <p className="text-base font-medium">
                                        No recent remits
                                    </p>
                                    <p className="text-textSec text-caption font-normal w-[70%] max-[768px]:w-[90%] flex-wrap leading-normal">
                                        No recent remit found. When you receive
                                        a remit, it will be displayed here
                                    </p>
                                </div>
                            )}
                            {billingStats?.data?.remits &&
                            billingStats?.data?.remits?.length > 0 ? (
                                <div className="flex flex-col gap-y-[--spacing-items]">
                                    {billingStats?.data?.remits
                                        ?.slice(0, 3)
                                        ?.map((remit, index) => (
                                            <div
                                                className="grid grid-cols-3 px-8 py-0 gap-x-2 rounded-none items-start justify-start text-caption text-left font-normal"
                                                key={`${index + 1} `}
                                            >
                                                <span>
                                                    {formatDate(
                                                        remit.date_of_service ||
                                                            ""
                                                    )}
                                                </span>
                                                <span>
                                                    {displayNameInRightFormat(
                                                        remit.client_name
                                                    )}
                                                </span>
                                                <span>
                                                    {showMoneyInAppFormat(
                                                        remit.amount
                                                    )}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            ) : null}
                        </div>
                    </Card>
                    <Card classNames="bg-[--bg-billed]">
                        <div className="mb-[--spacing-items] flex items-center justify-between">
                            <div className="flex items-center gap-x-2">
                                <h3 className="font-bold text-base md:text-base">
                                    Draft Claims
                                </h3>
                                <Tooltip content="Draft claims shows claims that are in progress or pending submission.">
                                    <Button size="auto" variant="normal">
                                        <InfoIcon />
                                    </Button>
                                </Tooltip>
                            </div>
                            <Link
                                to="/claims?navLinkInUrl=claims"
                                className="font-semibold text-textLink text-small"
                            >
                                View all claims
                            </Link>
                        </div>
                        <span className="text-textSec text-caption font-medium block mb-2">
                            Total Claims
                        </span>
                        <span className="font-bold text-titleLg block">
                            {billingStats?.data?.total_draft_claims || 0}
                        </span>
                        <div className="mt-[--spacing-header] text-xss">
                            <div className="rounded-md bg-[#DFEFF3] py-6 px-8 grid grid-cols-3 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                <span>Date of service</span>
                                <span>Client</span>
                                <span>CPT Code</span>
                            </div>
                            {billingStats?.data?.draft_claims?.length === 0 && (
                                <div className="flex flex-col gap-y-[--spacing-items]">
                                    <p className="text-base font-medium">
                                        No draft claims
                                    </p>
                                    <p className="text-textSec text-caption font-normal w-[70%] max-[768px]:w-[90%] flex-wrap leading-normal">
                                        No draft claims found. To generate a
                                        draft claim, go to the
                                        &quot;Claims&quot; section.
                                    </p>
                                </div>
                            )}
                            {billingStats?.data?.draft_claims &&
                            billingStats?.data?.draft_claims?.length > 0 ? (
                                <div className="flex flex-col gap-y-[--spacing-items]">
                                    {billingStats?.data?.draft_claims
                                        ?.slice(0, 3)
                                        ?.map((draft, index) => (
                                            <div
                                                className="grid grid-cols-3 px-8 py-0 gap-x-2 rounded-none items-start justify-start text-caption text-left font-normal"
                                                key={`${index + 1} `}
                                            >
                                                <span>
                                                    {formatDate(
                                                        draft.date_of_service ||
                                                            ""
                                                    )}
                                                </span>
                                                <span>
                                                    {displayNameInRightFormat(
                                                        draft.client_name
                                                    )}
                                                </span>
                                                <span>{draft.cpt_code}</span>
                                            </div>
                                        ))}
                                </div>
                            ) : null}
                        </div>
                    </Card>
                    <Card classNames="bg-[--bg-approved-claims]">
                        <div className="mb-[--spacing-items] flex items-center justify-between">
                            <div className="flex items-center gap-x-2">
                                <h3 className="font-bold text-base md:text-base">
                                    Expiring Policies
                                </h3>
                                <Tooltip content="Expiring policies shows patients with insurance policies nearing expiration. Or Shows patients that their insurance policies are about to expire">
                                    <Button size="auto" variant="normal">
                                        <InfoIcon />
                                    </Button>
                                </Tooltip>
                            </div>
                            <Button
                                size="auto"
                                variant="normal"
                                className="font-semibold !text-textLink text-small"
                                onClick={handlePolicyModalToggle}
                            >
                                View all expiring policies
                            </Button>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-textSec text-caption font-medium block">
                                Total Expiring Policies
                            </span>
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {dateFilter
                                            ? truncateString(
                                                  removeEnumUnderscore(
                                                      dateFilter
                                                  )
                                              )
                                            : "Date range"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent
                                    align="end"
                                    maxHeight={216}
                                    width={200}
                                    className="overflow-y-auto"
                                >
                                    {Object.values(ExpiryDateRange).map(
                                        (range) => (
                                            <DropdownItem
                                                key={range}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleExpiryDateRangeFilter(
                                                        range
                                                    );
                                                }}
                                            >
                                                {makeStringFirstLetterCapital(
                                                    removeEnumUnderscore(range)
                                                )}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>
                        </div>

                        <span className="font-bold text-titleLg block">
                            {expiringPolicyData?.data.length || 0}
                        </span>
                        <div className="mt-[--spacing-header]">
                            <div className="rounded-md bg-[#D9EAD8] py-6 px-8 grid grid-cols-4 gap-x-2 mb-[--spacing-items] text-dark opacity-70 font-normal text-[11px] text-left">
                                <span>Client</span>
                                <span>Accumulation end date</span>
                                <span>Policy end date</span>
                                <span>FAP end date</span>
                            </div>
                            {expiringPolicyData?.data.length === 0 && (
                                <div className="flex flex-col gap-y-[--spacing-items]">
                                    <p className="text-base font-medium">
                                        No expiring policies
                                    </p>
                                    <p className="text-textSec text-caption font-normal w-[70%] max-[768px]:w-[90%] flex-wrap leading-normal">
                                        Expiring policies will show up here as
                                        they near their expiration date.
                                    </p>
                                </div>
                            )}
                            {expiringPolicyData?.data &&
                            expiringPolicyData?.data.length > 0 ? (
                                <div className="flex flex-col gap-y-[--spacing-items]">
                                    {expiringPolicyData?.data
                                        ?.slice(0, 3)
                                        ?.map((policy, index) => (
                                            <div
                                                className="grid grid-cols-4 px-8 py-0 gap-x-2 rounded-none items-start justify-start text-caption text-left font-normal"
                                                key={`${index + 1} `}
                                            >
                                                <Link
                                                    to={`/clients/${policy.client_id}`}
                                                    className="text-textLink underline capitalize"
                                                >
                                                    {displayNameInRightFormat(
                                                        policy.client_name
                                                    )}
                                                </Link>
                                                <span>
                                                    {policy.accumulations_end_date
                                                        ? formatDate(
                                                              policy.accumulations_end_date,
                                                              true
                                                          )
                                                        : "--"}
                                                </span>

                                                <span>
                                                    {policy.policy_effective_end_date
                                                        ? formatDate(
                                                              policy.policy_effective_end_date,
                                                              true
                                                          )
                                                        : "--"}
                                                </span>

                                                <span>
                                                    {policy.financial_assistance_end_date
                                                        ? formatDate(
                                                              policy.financial_assistance_end_date,
                                                              true
                                                          )
                                                        : "--"}
                                                </span>
                                            </div>
                                        ))}
                                </div>
                            ) : null}
                        </div>
                    </Card>
                </div>
            )}
        </>
    );
}
